import type { ComponentChildren } from 'preact';
import type { Cart, DepositInfo } from '../types';
import { PromoCode } from './PromoCode';
import { depositCopy, formatMoney, summarizeDeposit } from '../lib/deposit-summary';

export { depositCopy, formatMoney, summarizeDeposit } from '../lib/deposit-summary';
export type { DepositSummaryFacts } from '../lib/deposit-summary';

/**
 * The order summary on the payment step: each line, the deposit-vs-pay-
 * in-full choice, the promo code, and what is due today versus at the
 * appointment.
 *
 * Used by the Stripe Payment Element step (P5.3), which shipped without
 * any of it — taking pay-in-full and promo codes away from exactly the
 * tenant it was built for. A faithful copy of the Card Element step's
 * summary (Payment.tsx), deliberately NOT shared with it: Payment.tsx
 * serves every other tenant, and this work is Be You's alone (Matt,
 * 2026-10-12; H22). Keep the two in step by hand if either changes.
 *
 * Display only: every figure comes from the server's quote (D68).
 */

export interface DepositSummaryProps {
  cart: Cart;
  deposit: DepositInfo;
  isBeYou: boolean;
  /** A payment or booking is in flight: the choice and promo are locked. */
  disabled: boolean;
  onSetPaymentMode?: (payInFull: boolean) => Promise<void>;
  onApplyPromo?: (code: string) => Promise<{ ok: boolean; message?: string }>;
  onRemovePromo?: () => Promise<void>;
  appliedPromoLabel?: string | null;
  /**
   * Rendered directly under the deposit-vs-pay-in-full choice — P5.3's
   * financing message, placed where the decision to pay in full is made.
   * Beside the choice rather than inside its button: the message is a
   * Stripe iframe, and an iframe over a button swallows the tap.
   */
  payInFullHint?: ComponentChildren;
}

export function DepositSummary({
  cart,
  deposit,
  isBeYou,
  disabled,
  onSetPaymentMode,
  onApplyPromo,
  onRemovePromo,
  appliedPromoLabel,
  payInFullHint,
}: DepositSummaryProps) {
  const {
    subtotalCents,
    discountCents,
    balanceCents,
    depositPct,
    payingInFull,
    sessionCount,
    perSessionCents,
    taxCents,
    taxPct,
    balanceIncludesTax,
  } = summarizeDeposit(cart, deposit, isBeYou);

  return (
    <div class="sbw-deposit-card">
      {cart.lines.map((line) => (
        <div key={line.lineId} class="sbw-deposit-row">
          <span>
            {line.serviceName}
            {perSessionCents !== null && line === cart.lines[0] && (
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  color: 'var(--sbw-ink-3)',
                  marginTop: 2,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {sessionCount} sessions · {formatMoney(perSessionCents, deposit.currency)} per
                session
              </span>
            )}
          </span>
          <span class="sbw-amt">
            {formatMoney(line.price.amountCents, line.price.currency)}
          </span>
        </div>
      ))}
      {/* P4 — deposit vs pay in full. Only offered when the tenant has
        turned it on AND the two figures actually differ; a "choice"
        between $50 and $50 is noise. Both numbers come from the
        server, so this control never does arithmetic on prices. */}
      {onSetPaymentMode &&
        deposit.allowPayInFull &&
        typeof deposit.fullAmountCents === 'number' &&
        typeof deposit.depositAmountCents === 'number' &&
        // Compare the two STABLE figures. Comparing against
        // `amountCents` meant that choosing "pay in full" made it equal
        // `fullAmountCents`, the condition went false, and the control
        // removed itself — stranding the customer on the choice they
        // had just made with no way back to the deposit.
        deposit.fullAmountCents > deposit.depositAmountCents && (
          <div class="sbw-paymode" role="group" aria-label="How much to pay now">
            <button
              type="button"
              class={`sbw-paymode__opt${!deposit.payInFull ? ' is-selected' : ''}`}
              aria-pressed={!deposit.payInFull}
              disabled={disabled}
              onClick={() => {
                void onSetPaymentMode(false);
              }}
            >
              <span class="sbw-paymode__amt">
                {formatMoney(deposit.depositAmountCents, deposit.currency)}
              </span>
              <span class="sbw-paymode__lbl">Deposit now</span>
            </button>
            <button
              type="button"
              class={`sbw-paymode__opt${deposit.payInFull ? ' is-selected' : ''}`}
              aria-pressed={deposit.payInFull === true}
              disabled={disabled}
              onClick={() => {
                void onSetPaymentMode(true);
              }}
            >
              <span class="sbw-paymode__amt">
                {formatMoney(deposit.fullAmountCents, deposit.currency)}
              </span>
              <span class="sbw-paymode__lbl">Pay in full</span>
            </button>
          </div>
        )}

      {payInFullHint}

      {onApplyPromo && onRemovePromo && (
        <PromoCode
          appliedCode={appliedPromoLabel ?? null}
          discountCents={discountCents}
          currency={deposit.currency}
          onApply={onApplyPromo}
          onRemove={onRemovePromo}
          disabled={disabled}
          formatMoney={formatMoney}
        />
      )}
      <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
        <span>Subtotal</span>
        <span class="sbw-amt">{formatMoney(subtotalCents, deposit.currency)}</span>
      </div>
      {taxCents > 0 && (
        <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
          <span>Sales tax{taxPct ? ` (${taxPct}%)` : ''}</span>
          <span class="sbw-amt">{formatMoney(taxCents, deposit.currency)}</span>
        </div>
      )}
      <div class="sbw-deposit-row is-emph">
        <span>
          {payingInFull ? 'Pay today' : 'Deposit today'}
          {depositPct !== null && !payingInFull && (
            <span
              style={{
                fontSize: 11,
                color: 'var(--sbw-ink-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                marginLeft: 6,
              }}
            >
              {depositPct}%
            </span>
          )}
        </span>
        <span class="sbw-amt">{formatMoney(deposit.amountCents, deposit.currency)}</span>
      </div>
      {!payingInFull && (
        <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)', fontSize: 12 }}>
          <span>Balance at appointment{balanceIncludesTax ? ' (incl. tax)' : ''}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(balanceCents, deposit.currency)}
          </span>
        </div>
      )}
    </div>
  );
}

export interface DepositHeaderProps {
  cart: Cart;
  deposit: DepositInfo;
  isBeYou: boolean;
  apptStartAt: string | null;
  timezone: string | null;
  staffName: string | null;
}

/**
 * The payment step's heading — eyebrow, title, sub-line, and when the
 * appointment is — for a deposit or pay-in-full payment. Same copy the
 * Card Element step shows.
 */
export function DepositHeader({
  cart,
  deposit,
  isBeYou,
  apptStartAt,
  timezone,
  staffName,
}: DepositHeaderProps) {
  const { title, sub } = depositCopy(deposit, summarizeDeposit(cart, deposit, isBeYou));
  return (
    <>
      {!isBeYou && <div class="sbw-eyebrow">Secure your slot</div>}
      <h2 class="sbw-step-title">{title}</h2>
      <p class="sbw-step-sub">{sub}</p>
      {apptStartAt && timezone && (
        <div class="sbw-payment-when">
          {new Date(apptStartAt).toLocaleString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone,
          })}
          {staffName ? ` · with ${staffName}` : ''}
        </div>
      )}
    </>
  );
}
